import {
  lineOf,
  type MonthRevenue,
  type RevenueOverview,
} from 'src/secret-menu/billing/hooks/useRevenue';
import { describeChange } from '../overview/lenses';
import { compare, lastMonthOf } from './history';
import {
  formatRevenue,
  formatSignedPence,
  formatSignedUnits,
  formatUnits,
  Stat,
  toneOf,
} from './ui';

/**
 * The revenue figures the Overview headline and the Revenue tab both show, so they read the same
 * wherever they appear. Each is today's figure, whichever month the Overview has selected.
 */

type Props = { overview: RevenueOverview; history: readonly MonthRevenue[]; today: Date };

const NO_SNAPSHOT = 'Change shows from the next month-end';

export function MrrStat({ overview, history, today }: Props) {
  const adviserMrr =
    lineOf(overview.today, 'ADVISER_PER_UNIT').mrrPence +
    lineOf(overview.today, 'ADVISER_FIXED').mrrPence;
  const change = compare(adviserMrr, lastMonthOf(history, today)?.adviserMrrPence ?? null);
  const growth =
    change.change === null
      ? ''
      : ` · advisers ${formatSignedPence(change.change)} (${describeChange(change.current, change.previous ?? 0).label}) since last month`;

  return (
    <Stat
      label="Monthly recurring revenue"
      value={formatRevenue(overview.today.mrrPence)}
      detail={`Today · ${formatRevenue(overview.today.arrPence)} a year${growth}`}
      tone={toneOf(change.change)}
      hint="monthlyRecurringRevenue"
    />
  );
}

export function BilledUnitsStat({ overview, history, today }: Props) {
  const units = compare(overview.billedUnits, lastMonthOf(history, today)?.billedUnits ?? null);
  return (
    <Stat
      label="Billable units"
      value={formatUnits(units.current)}
      detail={
        units.change === null
          ? `Today · ${NO_SNAPSHOT}`
          : `Today · ${formatSignedUnits(units.change)} (${describeChange(units.current, units.previous ?? 0).label}) since last month`
      }
      tone={toneOf(units.change)}
      hint="billedUnits"
    />
  );
}

export function FullAdoptionStat({ overview }: Pick<Props, 'overview'>) {
  const gap = overview.fullAdoption.mrrPence - overview.today.mrrPence;
  return (
    <Stat
      label="At full adoption"
      value={formatRevenue(overview.fullAdoption.mrrPence)}
      detail={`${formatSignedPence(gap)} a month on today · Strategic partner ${formatRevenue(overview.monumentFullAdoption.totalPence)}`}
      tone={toneOf(gap)}
      hint="fullAdoption"
    />
  );
}
